<template>
  <div>
    <!-- ISEC: claimed area of the farmer's herd -->
    <div ref="areaDiv" v-show="ui.showArea" class="esri-widget p-2">
      <div>
        <span class="font-extrabold">LPIS Area: </span>
        <span>{{ ui.areaText }}</span>
      </div>
      <div v-if="$root.Lpis.show_zone">
        <span class="font-extrabold">Zones:</span>
        <span>{{ $root.farmer?.count || 0 }}</span>
      </div>
    </div>

    <!-- ISEC: herd numbers of the clicked parcel -->
    <div ref="herdDiv" v-show="ui.herdNos.length" class="esri-widget lpis-panel p-2">
      <div class="font-extrabold">Herd No.</div>
      <div v-for="herd in ui.herdNos" :key="herd">{{ herd }}</div>
    </div>

    <!-- DAFM: details of the clicked parcel -->
    <div ref="parcelDiv" v-show="ui.parcel && !ui.linking" class="esri-widget lpis-panel p-3 text-sm">
      <template v-if="ui.parcel">
        <div class="flex justify-between items-start">
          <span class="font-extrabold">{{ ui.parcel.title }}</span>
          <button
            type="button"
            class="-mr-2 -mt-2 ml-3 w-8 h-8 flex-none rounded-full text-xl leading-none text-gray-500 hover:bg-gray-100"
            @click="clearSelection"
            aria-label="Close"
          >
            &times;
          </button>
        </div>
        <div v-for="row in ui.parcel.rows" :key="row[0]">
          <span class="font-semibold">{{ row[0] }}: </span>{{ row[1] }}
        </div>
      </template>
    </div>

    <!-- DAFM: linking parcels to the farmer, at the bottom left. Phones get a
         compact full-width version, with the help and totals left out. -->
    <section v-show="ui.linking" class="lpis-link-panel" aria-labelledby="lpis-link-title">
      <!-- Above the panel, while the map is too far out for the grey parcels -->
      <p
        v-show="ui.zoomedOut"
        class="mb-2 w-fit mx-auto sm:mx-0 rounded-lg bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 shadow-lg border border-amber-200"
        role="status"
      >
        Zoom in to see and select LPIS parcels.
      </p>
      <div class="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden text-sm">
        <header class="flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-3 bg-[#1E660B] text-white">
          <h3 id="lpis-link-title" class="font-semibold text-sm sm:text-base flex-1">Link LPIS parcels</h3>
          <span class="rounded-full bg-white/20 px-2 py-0.5 text-xs font-semibold whitespace-nowrap">
            {{ ui.selectionSummary?.count || 0 }} selected
          </span>
          <button
            type="button"
            class="w-7 h-7 sm:w-8 sm:h-8 rounded-full hover:bg-white/20 font-bold"
            :aria-expanded="ui.linkHelp"
            aria-label="How linking works"
            @click="ui.linkHelp = !ui.linkHelp"
          >
            ?
          </button>
        </header>

        <div class="lpis-link-body p-3 space-y-2 sm:p-4 sm:space-y-3">
          <p v-show="ui.linkHelp" class="text-gray-600">
            Select a parcel to add it, or a pink parcel to remove it. The selection mode sets whether the
            parcel's whole holding is added or removed with it. Commonage and unclaimed parcels always go one
            at a time.
          </p>

          <fieldset>
            <legend class="sr-only sm:not-sr-only sm:mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Selection mode
            </legend>
            <div class="grid grid-cols-2 rounded-lg border border-gray-300 overflow-hidden">
              <label
                v-for="option in selectOptions"
                :key="option.value"
                class="flex items-center justify-center py-1.5 sm:py-2 cursor-pointer font-medium focus-within:ring-2 focus-within:ring-inset focus-within:ring-[#1E660B]"
                :class="ui.selectMode === option.value ? 'bg-[#1E660B] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
              >
                <input type="radio" class="sr-only" name="lpis-select-mode" :value="option.value" v-model="ui.selectMode" />
                <span class="sm:hidden">{{ option.short }}</span>
                <span class="hidden sm:inline">{{ option.label }}</span>
              </label>
            </div>
          </fieldset>

          <div class="hidden sm:block rounded-lg bg-gray-50 px-3 py-2 font-medium text-gray-800">
            {{ selectionText(ui.selectionSummary) }}
          </div>

          <p v-if="ui.linkMessage" class="rounded-lg bg-amber-50 px-3 py-2 text-amber-800" role="status">
            {{ ui.linkMessage }}
          </p>

          <!-- One row of four on phones; Save gets its own row on wider screens -->
          <div class="grid grid-cols-4 gap-1.5 sm:grid-cols-3 sm:gap-2">
            <button
              type="button"
              class="lpis-link-button"
              :disabled="ui.saving || ui.linkBusy || !canUndo"
              @click="undoLast"
            >
              Undo
            </button>
            <button type="button" class="lpis-link-button" :disabled="ui.saving || ui.linkBusy" @click="clearAll">
              Clear all
            </button>
            <button type="button" class="lpis-link-button" :disabled="ui.saving" @click="exitLinking">
              Cancel
            </button>
            <button
              type="button"
              class="min-h-[2.25rem] sm:min-h-[2.75rem] sm:col-span-3 rounded-lg bg-[#1E660B] text-white sm:text-base font-semibold hover:bg-[#17500a] disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="ui.saving || ui.linkBusy"
              @click="saveLinking"
            >
              {{ ui.saving ? "Saving..." : "Save" }}
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- ISEC: show or hide herds whose tasks are complete -->
    <div ref="toggleDiv" v-show="ui.showToggle" class="esri-widget flex p-2">
      <p class="inline-flex items-center mr-2">Toggle Completed LPIS</p>

      <label class="inline-flex items-center cursor-pointer">
        <input type="checkbox" value="" class="sr-only peer" v-model="toggleCompletedLPIS" />
        <div
          class="relative w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-green"
        ></div>
      </label>
    </div>
  </div>
</template>
<script setup>
import { computed, getCurrentInstance, inject, onBeforeUnmount, onMounted, reactive, ref, shallowRef, watch } from "vue";
import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import GroupLayer from "@arcgis/core/layers/GroupLayer";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import { useToast } from "vue-toastification";
import { Inertia } from "@inertiajs/inertia";
import LPISLayerBuilder, { cleanIds, sqlString } from "./Util/LPISLayerBuilder";
import DafmLpisSource from "./Util/DafmLpisSource";
import { extentOf } from "./Util/extent";

const MAX_LINKED_PARCELS = 1000;
// Matches Tailwind's sm: breakpoint, used by the link panel's layout
const PHONE_QUERY = "(max-width: 639px)";

const hectares = (value) => `${value.toFixed(2)} ha`;
const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

const props = defineProps({
  view: Object,
  map: Object,
  // "isec" or "dafm"; when empty and LPIS is shown, ISEC is assumed
  source: String,
  showLpis: [Boolean, String],
  lpisUrl: String,
  dafm: Object, // { available, cell_size }, only for signed-in users
  LpisHerdNo: String,
  linkedIds: Array,
  // true while another map tool owns clicks (drawing, georeferencing)
  isMapBusy: Function,
  // DAFM only: whether the grey parcels show outside linking (layer toggle)
  showContext: Boolean,
  // DAFM only: whether the farmer's pink parcels show outside linking (layer toggle)
  showLinked: { type: Boolean, default: true },
});

const emit = defineEmits(["layerLoaded", "filteredFeaturesReady", "linkedFeaturesReady"]);

const internalInstance = getCurrentInstance();
const $emitter = internalInstance.appContext.config.globalProperties.$emitter;
const rootData = () => internalInstance.root.data;
const toast = useToast();
const swal = inject("$swal");

const areaDiv = ref(null);
const herdDiv = ref(null);
const parcelDiv = ref(null);
const toggleDiv = ref(null);

const ui = reactive({
  showArea: false,
  areaText: "0.00 ha",
  summary: null,
  herdNos: [],
  parcel: null,
  showToggle: false,
  linking: false,
  linkMessage: "",
  zoomedOut: false, // while linking: zoomed out past the grey parcels
  selectionSummary: null,
  saving: false,
  selectMode: "holding", // what a click adds or removes: the "holding" or the "parcel"
  linkBusy: false, // a link click is still looking up parcels
  linkHelp: true, // the link panel's help text; hidden at first on phones
});

const selectOptions = [
  { value: "holding", label: "Whole holding", short: "Holding" },
  { value: "parcel", label: "Single parcel", short: "Parcel" },
];

// DAFM layer visibility: the layer toggles, except that linking always shows
// the grey parcels and draws the farmer's parcels as the selection instead
const contextShown = computed(() => ui.linking || props.showContext);
const linkedShown = computed(() => !ui.linking && props.showLinked);

function applyDafmVisibility({ load = true } = {}) {
  if (active?.source !== "dafm") return;
  active.builder.setContextVisible(contextShown.value, { load });
  active.builder.getLayers().linked.visible = linkedShown.value;
}

const toggleCompletedLPIS = ref(false);
let completedWhere = null;

const builders = {};
let active = null; // { source, builder }
let refreshToken = 0;
let highlights = [];
let highlightToken = 0;
// Changes with each parcel click or close (clearSelection)
let clickToken = 0;
// While linking: watches the map scale for the panel's zoom hint
let scaleHandle = null;
let clickHandle = null;

// All LPIS layers, kept as one group at the bottom of the map in the order of
// lpisLayers(), so livefarm layers added later always draw above them
const lpisGroup = new GroupLayer({ title: "LPIS", listMode: "hide" });

// Linking state
const selectionLayer = new GraphicsLayer({ title: "LPIS selection", listMode: "hide" });
// DAFM: the clicked parcel's holding, drawn at every zoom so a holding spread
// across several areas shows in full, even where its parcels aren't loaded
const highlightLayer = new GraphicsLayer({ title: "LPIS highlight", listMode: "hide" });
const highlightSymbol = {
  type: "simple-fill",
  style: "solid",
  color: [0, 255, 255, 0.15],
  outline: { color: [0, 255, 255], width: 2.5 },
};
const selection = new Map(); // lpis id -> { feature, graphic }
let linkingFarmerHashid = null;
// A request to start linking that arrived before the DAFM layer was ready
let pendingLinking = null;
// Undo steps, newest last: { added: [ids], removed: [features] }. Shallow,
// so Vue doesn't wrap the ArcGIS graphics; changes replace the array.
const history = shallowRef([]);
const canUndo = computed(() => history.value.length > 0);
// Changes when linking starts or ends, so late lookups can be dropped
let linkSession = 0;
// True while startLinking loads the farmer's existing links
let linkStarting = false;
// The farmer's linked parcels as last loaded, keyed by farmer and ids, so
// starting or saving a link doesn't fetch them again
let linkedFeatures = { key: null, features: [] };

const selectionSymbol = {
  type: "simple-fill",
  style: "solid",
  color: [242, 105, 237, 0.35],
  outline: { color: [242, 105, 237], width: 2.5 },
};

// Selected commonage keeps a yellow edge so it still reads as commonage
const selectionCommonageSymbol = {
  ...selectionSymbol,
  outline: { color: [255, 255, 0], width: 2.5 },
};

function builderFor(source) {
  if (builders[source]) return builders[source];
  if (source === "isec" && props.lpisUrl) {
    builders.isec = new LPISLayerBuilder(props.lpisUrl);
  }
  if (source === "dafm" && props.dafm?.available) {
    builders.dafm = new DafmLpisSource(props.dafm.cell_size, () => props.dafm?.version);
  }
  return builders[source] || null;
}

function wantedSource() {
  if (!props.showLpis) return null;
  return props.source || "isec";
}

// The LPIS layers in stacking order: grey parcels, the farmer's pink parcels,
// the highlight, the link selection
function lpisLayers() {
  if (!active) return [];
  const { context, linked } = active.builder.getLayers();
  return [context, linked, highlightLayer, selectionLayer];
}

function detach() {
  if (!active) return;
  if (ui.linking) exitLinking();
  active.builder.unwatch?.();
  lpisGroup.removeAll();
  clearSelection();
  ui.showArea = false;
  ui.summary = null;
  ui.showToggle = false;
  completedWhere = null;
  active = null;
  emit("layerLoaded", null, []);
}

async function refresh() {
  const { map, view } = props;
  if (!map || !view) return;
  const token = ++refreshToken;

  const source = wantedSource();
  const builder = source ? builderFor(source) : null;

  if (active && active.builder !== builder) detach();
  if (!builder) return;

  // LPIS sits in one group at the bottom of the stack, under every livefarm layer
  if (!map.layers.includes(lpisGroup)) map.add(lpisGroup, 0);

  if (!active) {
    active = { source, builder };
    const { context, linked } = builder.getLayers();
    lpisGroup.addMany(source === "dafm" ? [context, linked, highlightLayer] : [context, linked]);
    builder.watchView?.(view);
    emit("layerLoaded", context, lpisLayers());
  }
  applyDafmVisibility();

  try {
    if (source === "isec") {
      await refreshIsec(builder, token);
    } else {
      await refreshDafm(builder, token);
    }
  } catch (error) {
    console.error("Error loading LPIS parcels: ", error);
  }

  if (pendingLinking && active?.source === "dafm" && token === refreshToken) {
    const payload = pendingLinking;
    pendingLinking = null;
    // A request left over from another farmer's page is dropped
    if (!payload.farmerHashid || payload.farmerHashid === rootData().farmer?.hashid) startLinking(payload);
  }
}

async function refreshIsec(builder, token) {
  const herd = props.LpisHerdNo;
  builder.setLinkedWhere(herd ? builder.herdWhere(herd) : "1=0");
  ui.summary = null;

  if (!herd) {
    ui.showArea = false;
    return;
  }

  const lpis = rootData().Lpis;
  const { totalAreaClaim, locations } = await builder.calculateTotalAreaClaim(herd);
  if (token !== refreshToken) return;

  if (locations.length > 0) {
    emit("filteredFeaturesReady", locations);
    props.view.goTo(locations).catch(() => {});
  }

  ui.areaText = hectares(totalAreaClaim);
  ui.showArea = totalAreaClaim > 0 && !!lpis.showLpisAreaDetails;

  if (lpis.filterCompletedHerdNo) {
    filterCompletedFarms(builder);
  }
}

async function refreshDafm(builder, token) {
  ui.showArea = false;
  ui.showToggle = false;

  const farmerHashid = rootData().farmer?.hashid;
  const features = await linkedFeaturesOf(builder, farmerHashid);
  if (token !== refreshToken) return;

  await builder.replaceFeatures(builder.getLayers().linked, features);
  if (token !== refreshToken) return;

  ui.summary = features.length ? summarise(builder, features) : null;
  // Emitted with none too, so a farmer whose links were all removed loses
  // them from the farmer zoom
  if (farmerHashid) emit("linkedFeaturesReady", features, farmerHashid);
}

const linkedKey = (farmerHashid, ids) =>
  `${farmerHashid}:${cleanIds(ids).sort((a, b) => a - b).join(",")}`;

async function linkedFeaturesOf(builder, farmerHashid) {
  if (!farmerHashid || !cleanIds(props.linkedIds).length) return [];
  const key = linkedKey(farmerHashid, props.linkedIds);
  if (linkedFeatures.key !== key) {
    linkedFeatures = { key, features: await builder.fetchLinked(farmerHashid) };
  }
  return linkedFeatures.features;
}

// Hectares claimed by the farmer, hectares not claimed by anyone, and the
// commonage shapes that belong to another holding (counted, area left out).
function summarise(builder, features) {
  const attrs = (feature) => feature.attributes || {};
  const ownHerds = new Set(
    features
      .filter((feature) => attrs(feature).app_herd && attrs(feature).commonage_ind !== "Y")
      .map((feature) => attrs(feature).app_herd)
  );

  let claimed = 0;
  let notClaimed = 0;
  let shared = 0;
  features.forEach((feature) => {
    const a = attrs(feature);
    if (!a.app_herd) {
      notClaimed += builder.areaOf(feature);
    } else if (a.commonage_ind === "Y" && !ownHerds.has(a.app_herd)) {
      shared += 1;
    } else {
      claimed += Number(a.claim_area) || 0;
    }
  });

  return { count: features.length, claimed, notClaimed, shared, ownHerds };
}

function summaryText(summary) {
  let text = `${plural(summary.count, "parcel")}, ${summary.claimed.toFixed(1)} ha claimed`;
  if (summary.notClaimed > 0) text += `, ${summary.notClaimed.toFixed(1)} ha not claimed`;
  if (summary.shared > 0) text += `, ${summary.shared} shared commonage`;
  return text;
}

function selectionText(summary) {
  return summary ? summaryText(summary) : "No parcels selected";
}

// ISEC: hide herds whose tasks are already collected or complete
async function filterCompletedFarms(builder) {
  try {
    const response = await axios.get(route("app.arcgis.completed_lpis"));
    const herds = response.data.filter((herd) => herd !== null && herd !== "");
    completedWhere = herds.length
      ? `${builder.herdField} NOT IN (${herds.map((herd) => sqlString(String(herd).trim())).join(",")})`
      : null;
    toggleCompletedLPIS.value = false;
    ui.showToggle = true;
    applyCompletedFilter();
  } catch (error) {
    console.error("Error loading completed LPIS herds: ", error);
  }
}

function applyCompletedFilter() {
  if (!active || active.source !== "isec") return;
  const { context } = active.builder.getLayers();
  const where = toggleCompletedLPIS.value ? null : completedWhere;
  props.view.whenLayerView(context).then((layerView) => {
    layerView.filter = where ? { where } : null;
  });
}

watch(toggleCompletedLPIS, applyCompletedFilter);

function clearHighlights() {
  highlightToken += 1;
  highlightLayer.removeAll();
  highlights.forEach((handle) => handle.remove());
  highlights = [];
}

function clearSelection() {
  // Lookups still running for an earlier click see this and stop
  clickToken += 1;
  clearHighlights();
  ui.parcel = null;
  ui.herdNos = [];
}

// Highlight parcels. DAFM draws them on their own layer and brings them into
// view; ISEC highlights them on both layers, wherever they are loaded.
function highlight(features) {
  clearHighlights();
  if (!active || !features.length) return;
  if (active.source === "dafm") {
    const geometries = features.map((feature) => feature.geometry).filter(Boolean);
    highlightLayer.addMany(geometries.map((geometry) => new Graphic({ geometry, symbol: highlightSymbol })));
    goToIfOutside(extentOf(geometries));
    return;
  }
  const token = highlightToken;
  const builder = active.builder;
  const { context, linked } = builder.getLayers();
  [context, linked].forEach((layer) => {
    Promise.all([props.view.whenLayerView(layer), builder.objectIdsIn(layer, features)])
      .then(([layerView, objectIds]) => {
        if (!objectIds.length) return;
        const handle = layerView.highlight(objectIds);
        // A later click may have cleared the highlight while this one loaded
        if (token === highlightToken) highlights.push(handle);
        else handle.remove();
      })
      .catch(() => {});
  });
}

const idOf = (feature) => Number(feature.attributes?.id);
const isCommonage = (feature) => feature.attributes?.commonage_ind === "Y";
const zoomedIn = () => props.view.scale <= active.builder.minScale;
// Whether the grey parcels are on screen: zoomed in, and for DAFM switched on
const seesContext = () => zoomedIn() && (active.source !== "dafm" || contextShown.value);

async function onClick(event) {
  if (!active) return;
  if (!ui.linking && props.isMapBusy?.()) return;

  // Leave clicks on livefarm parcels, samples and markers to their own handlers
  let hits = [];
  if (!ui.linking) {
    const response = await props.view.hitTest(event);
    const top = response.results[0];
    if (top && !lpisLayers().includes(top.layer)) return;
    // A second click on the highlighted holding clears it
    if (top?.layer === highlightLayer) {
      clearSelection();
      return;
    }
    // Without the grey parcels, only the farmer's are drawn; a miss has nothing to show
    if (!top && !seesContext()) {
      clearSelection();
      return;
    }
    hits = response.results;
  }

  try {
    if (ui.linking) {
      await handleLinkClick(event.mapPoint);
    } else if (active.source === "isec") {
      await handleIsecClick(hits);
    } else {
      await handleDafmClick(event.mapPoint);
    }
  } catch (error) {
    console.error("Error handling LPIS click: ", error);
  }
}

async function handleIsecClick(hits) {
  const shownHerd = ui.herdNos[0];
  clearSelection();
  const { context, linked } = active.builder.getLayers();
  const herdNos = [
    ...new Set(
      hits
        .filter((hit) => hit.layer === context || hit.layer === linked)
        .map((hit) => hit.graphic?.attributes?.app_herd)
        .filter(Boolean)
    ),
  ];
  // No herd, or a second click on the highlighted herd, leaves it cleared
  if (!herdNos.length || herdNos[0] === shownHerd) return;

  ui.herdNos = herdNos;
  const token = clickToken;
  const holding = await active.builder.queryHolding(herdNos[0]);
  // A newer click or a close wins over this one
  if (token === clickToken) highlight(holding);
}

async function handleDafmClick(mapPoint) {
  clearSelection();
  const token = clickToken;
  const builder = active.builder;
  let rows = await builder.queryAtPoint(mapPoint);
  // A newer click or a close wins over this one
  if (token !== clickToken) return;

  // Without the grey parcels, only the farmer's own pink parcels are on the map
  const linkedSet = new Set(cleanIds(props.linkedIds));
  if (!seesContext()) {
    rows = rows.filter((row) => linkedSet.has(idOf(row)));
  }
  if (!rows.length) return;

  const pick =
    rows.find((row) => linkedSet.has(idOf(row)) && !isCommonage(row)) ||
    rows.find((row) => row.attributes.app_herd && !isCommonage(row)) ||
    rows.find(isCommonage) ||
    rows[0];
  const a = pick.attributes;
  const claimedArea = (features) => features.reduce((sum, row) => sum + (Number(row.attributes.claim_area) || 0), 0);

  // Every kind of parcel shows the same rows, with "-" where a row doesn't apply
  let title = "Not claimed";
  let claimed = "-";
  let holdingText = "-";
  let commonageText = "No";
  let highlighted = [pick];

  if (isCommonage(pick)) {
    // One copy per shareholder; the claimed area is all their shares together
    const copies = rows.filter(isCommonage);
    title = "Commonage";
    claimed = hectares(claimedArea(copies));
    commonageText = `Yes, shared by ${plural(copies.length, "herd")}`;
    highlighted = copies;
  } else if (a.app_herd) {
    const holding = await builder.queryHolding(a.app_herd);
    if (token !== clickToken) return;
    title = "Claimed parcel";
    claimed = hectares(Number(a.claim_area) || 0);
    holdingText = `${plural(holding.length, "parcel")}, ${hectares(claimedArea(holding))} claimed`;
    highlighted = holding;
  }

  // One of the farmer's own parcels also shows the totals for all of them
  const farmerRows =
    linkedSet.has(idOf(pick)) && ui.summary ? [["Farmer's LPIS", summaryText(ui.summary)]] : [];

  ui.parcel = {
    title,
    rows: [
      ...farmerRows,
      ["Parcel size", hectares(builder.areaOf(pick))],
      ["Claimed", claimed],
      ["Crop", a.crop || "-"],
      ["Ownership", a.olr || "-"],
      ["Holding", holdingText],
      ["Commonage", commonageText],
    ],
  };
  highlight(highlighted);
}

// ---- Linking ----

function selectedFeatures() {
  return [...selection.values()].map((entry) => entry.feature);
}

function updateSelectionSummary() {
  ui.selectionSummary = selection.size ? summarise(active.builder, selectedFeatures()) : null;
}

// Adds parcels to the selection; returns the ids actually added
function addFeatures(features) {
  const added = [];
  const graphics = [];
  let skipped = 0;
  features.forEach((feature) => {
    const id = idOf(feature);
    if (!id || selection.has(id)) return;
    if (selection.size >= MAX_LINKED_PARCELS) {
      skipped += 1;
      return;
    }
    const symbol = isCommonage(feature) ? selectionCommonageSymbol : selectionSymbol;
    const graphic = new Graphic({ geometry: feature.geometry, symbol });
    graphics.push(graphic);
    selection.set(id, { feature, graphic });
    added.push(id);
  });
  if (skipped) {
    ui.linkMessage = `A farmer can have at most ${MAX_LINKED_PARCELS} LPIS parcels.`;
  }
  selectionLayer.addMany(graphics);
  updateSelectionSummary();
  return added;
}

function removeIds(ids) {
  const graphics = [];
  ids.forEach((id) => {
    const entry = selection.get(id);
    if (!entry) return;
    graphics.push(entry.graphic);
    selection.delete(id);
  });
  selectionLayer.removeMany(graphics);
  updateSelectionSummary();
}

function clearLinkSelection() {
  selection.clear();
  selectionLayer.removeAll();
  history.value = [];
  ui.linkMessage = "";
}

// ---- Undo ----

function pushHistory(entry) {
  if (entry.added.length || entry.removed.length) history.value = [...history.value, entry];
}

function undoLast() {
  if (ui.linkBusy || ui.saving) return;
  const entry = history.value[history.value.length - 1];
  history.value = history.value.slice(0, -1);
  ui.linkMessage = "";
  if (!entry) return;
  const undone = entry.added.map((id) => selection.get(id)?.feature).filter(Boolean);
  removeIds(entry.added);
  fitSelection(addFeatures(entry.removed), undone);
}

async function startLinking(payload = {}) {
  if (!active || active.source !== "dafm") {
    // The page may ask before the map has switched to DAFM; start once it has
    pendingLinking = payload;
    return;
  }
  if (ui.linking || linkStarting) return;

  linkingFarmerHashid = payload.farmerHashid || rootData().farmer?.hashid;
  if (!linkingFarmerHashid) return;

  // Linking owns map clicks, so any drawing or editing in progress ends
  $emitter.emit("cancelFarmInteraction");

  const session = ++linkSession;
  ui.selectMode = "holding";
  clearSelection();
  clearLinkSelection();

  // The farmer's existing links are the starting point, not an undo step.
  // When the page's farmer data isn't this farmer's, or hasn't loaded yet,
  // they are fetched for this farmer, so Save never replaces their links
  // with a partial set.
  const builder = active.builder;
  const farmer = rootData().farmer;
  const pageHasLinks = farmer?.hashid === linkingFarmerHashid && Array.isArray(farmer.lpis_linked_ids);
  let existing;
  linkStarting = true;
  try {
    existing = pageHasLinks
      ? await linkedFeaturesOf(builder, linkingFarmerHashid)
      : await builder.fetchLinked(linkingFarmerHashid);
  } catch (error) {
    console.error("Error loading the farmer's LPIS parcels: ", error);
    toast.error("The farmer's LPIS parcels couldn't be loaded. Please try again.");
    if (session === linkSession) linkingFarmerHashid = null;
    return;
  } finally {
    linkStarting = false;
  }
  // A cancel or navigation while loading drops this start
  if (session !== linkSession || active?.builder !== builder) return;
  addFeatures(existing);

  if (!lpisGroup.layers.includes(selectionLayer)) lpisGroup.add(selectionLayer);
  // Phones have little room, so the help starts folded away there
  ui.linkHelp = !window.matchMedia(PHONE_QUERY).matches;
  ui.linking = true;
  // Linking ends with Save or Cancel, so the phone's map/list switch hides
  rootData().lpisLinking = true;

  // A note above the panel asks for a zoom in whenever the grey parcels can't show
  scaleHandle = reactiveUtils.watch(
    () => !zoomedIn(),
    (out) => (ui.zoomedOut = out),
    { initial: true }
  );

  // Go to the selection, or else the farm. A selection too spread out for the
  // grey parcels to show gets the zoom-in note instead.
  const farm = (rootData().farmer?.farms || []).find((item) => item.location_xy?.length === 2);
  let target = null;
  if (selectionLayer.graphics.length) {
    target = selectionLayer.graphics.toArray();
  } else if (farm) {
    const [x, y] = farm.location_xy;
    target = { target: new Point({ x, y, spatialReference: { wkid: 3857 } }), scale: builder.minScale / 2 };
  }

  // Linking shows the grey parcels. When the map is about to move, they load
  // once it arrives, not for the place it is leaving.
  applyDafmVisibility({ load: !target });
  if (target) props.view.goTo(target).catch(() => {});
}

// One click at a time: lookups are awaited, and Undo, Clear all and Save
// wait for them, so steps can't interleave
async function handleLinkClick(mapPoint) {
  // While saving, the selection already sent must not change
  if (ui.linkBusy || ui.saving) return;
  ui.linkBusy = true;
  const session = linkSession;
  try {
    await applyLinkClick(mapPoint, () => session === linkSession);
  } finally {
    ui.linkBusy = false;
  }
}

async function applyLinkClick(mapPoint, stillLinking) {
  const builder = active.builder;
  ui.linkMessage = "";
  const rows = await builder.queryAtPoint(mapPoint);
  if (!stillLinking() || !rows.length) return;

  // 1. A selected parcel under the click is removed, with its whole holding
  //    in holding mode. Commonage and unclaimed parcels go one at a time.
  const selectedRow = rows.find((row) => selection.has(idOf(row)));
  if (selectedRow) {
    const herd = selectedRow.attributes.app_herd;
    const wholeHolding = ui.selectMode === "holding" && herd && !isCommonage(selectedRow);
    const removed = wholeHolding
      ? selectedFeatures().filter((feature) => feature.attributes.app_herd === herd)
      : [selection.get(idOf(selectedRow)).feature];
    removeIds(removed.map(idOf));
    pushHistory({ added: [], removed });
    fitSelection([], removed);
    return;
  }

  // The note above the panel already asks for a zoom in
  if (!zoomedIn()) return;

  const ownHerds = ui.selectionSummary?.ownHerds || new Set();

  // 2. The commonage copy that belongs to a holding already selected
  const ownCopy = rows.find((row) => isCommonage(row) && ownHerds.has(row.attributes.app_herd));
  // 3. Otherwise any commonage copy, as a shared parcel
  const commonage = ownCopy || rows.find(isCommonage);
  if (commonage) {
    addAndShow([commonage]);
    return;
  }

  // 4. A claimed parcel adds its whole holding, commonage included, or in
  //    parcel mode just itself
  const claimed = rows.find((row) => row.attributes.app_herd);
  if (claimed && ui.selectMode === "parcel") {
    addAndShow([claimed]);
    return;
  }
  if (claimed) {
    const holding = await builder.queryHolding(claimed.attributes.app_herd);
    if (!stillLinking()) return;
    addAndShow(holding);
    return;
  }

  // 5. An unclaimed parcel is added on its own
  addAndShow([rows[0]]);
}

// Brings an extent into view when any of it is outside the view
function goToIfOutside(extent) {
  if (extent && !props.view.extent.contains(extent)) {
    props.view.goTo(extent.expand(1.1)).catch(() => {});
  }
}

// Keeps the selection in view after a change. Added parcels outside the view
// make the map fit the whole selection, so a holding spread across several
// areas shows in full. Removed parcels that stretched the selection make the
// map fit what is left.
function fitSelection(addedIds, removedFeatures) {
  const remaining = extentOf(selectionLayer.graphics.toArray().map((graphic) => graphic.geometry));
  if (!remaining) return;

  const added = extentOf(addedIds.map((id) => selection.get(id)?.graphic.geometry));
  const removed = extentOf(removedFeatures.map((feature) => feature.geometry));
  const outOfView = added && !props.view.extent.contains(added);
  const shrunk = removed && !remaining.contains(removed);
  if (outOfView || shrunk) {
    props.view.goTo(remaining.expand(1.1)).catch(() => {});
  }
}

// Commonage is stored once per shareholder: copies of one shape have their
// own ids but the same measured area and outline
function sameShape(a, b) {
  const ea = a.geometry?.extent;
  const eb = b.geometry?.extent;
  if (!ea || !eb || a.attributes.area_ha !== b.attributes.area_ha) return false;
  return ["xmin", "ymin", "xmax", "ymax"].every((side) => Math.abs(ea[side] - eb[side]) < 0.5);
}

// Adds parcels as one Undo step. A commonage shape already selected as
// another holding's copy gives way to this holding's own copy, so the shape
// isn't selected twice.
function addAndShow(features) {
  const ownHerds = new Set(ui.selectionSummary?.ownHerds || []);
  features
    .filter((feature) => feature.attributes.app_herd && !isCommonage(feature))
    .forEach((feature) => ownHerds.add(feature.attributes.app_herd));
  const incomingCommonage = features.filter(isCommonage);
  const replaced = selectedFeatures().filter(
    (selected) =>
      isCommonage(selected) &&
      !ownHerds.has(selected.attributes.app_herd) &&
      incomingCommonage.some((feature) => idOf(feature) !== idOf(selected) && sameShape(feature, selected))
  );
  removeIds(replaced.map(idOf));

  const added = addFeatures(features);
  pushHistory({ added, removed: replaced });
  fitSelection(added, replaced);
}

async function clearAll() {
  if (ui.linkBusy || ui.saving || !selection.size) return;
  const count = selection.size;
  const { isConfirmed } = await swal.fire({
    title: `Remove all ${plural(count, "selected parcel")}?`,
    text: "This can't be undone.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Remove all",
  });
  if (!isConfirmed) return;

  clearLinkSelection();
  updateSelectionSummary();
}

function exitLinking() {
  linkSession += 1;
  clearLinkSelection();
  lpisGroup.remove(selectionLayer);
  scaleHandle?.remove();
  scaleHandle = null;
  ui.linking = false;
  applyDafmVisibility();
  ui.selectionSummary = null;
  ui.selectMode = "holding";
  linkingFarmerHashid = null;
  rootData().lpisLinking = false;
}

async function saveLinking() {
  if (!linkingFarmerHashid) return;
  // Kept before the request: a cancel or navigation during it clears the
  // linking state, and the saved links must still be recorded correctly
  const farmerHashid = linkingFarmerHashid;
  const features = selectedFeatures();
  // The server reads each parcel's herd code from the LPIS file
  const parcels = features.map((feature) => ({ lpis_id: idOf(feature) }));

  ui.saving = true;
  try {
    const response = await axios.put(route("app.farmer.lpis-parcels", farmerHashid), { parcels });
    const ids = response.data.lpis_linked_ids;
    // The saved parcels are already loaded, so the map draws them without a fetch
    linkedFeatures = { key: linkedKey(farmerHashid, ids), features };
    const farmer = rootData().farmer;
    if (farmer && farmer.hashid === farmerHashid) {
      farmer.lpis_linked_ids = ids;
    }
    toast.success("LPIS parcels saved.");
    if (ui.linking && linkingFarmerHashid === farmerHashid) exitLinking();
  } catch (error) {
    console.error("Error saving LPIS parcels: ", error);
    toast.error("The LPIS parcels couldn't be saved. Please try again.");
  } finally {
    ui.saving = false;
  }
}

// ---- Wiring ----

// Panels move into the map's UI and the click handler is registered once per view
function attachToView(view) {
  if (!view || !herdDiv.value) return;
  view.ui.add(herdDiv.value, "bottom-left");
  view.ui.add(areaDiv.value, "bottom-left");
  view.ui.add(parcelDiv.value, "bottom-left");
  view.ui.add(toggleDiv.value, "bottom-left");
  clickHandle?.remove();
  clickHandle = view.on("click", onClick);
}

onMounted(() => attachToView(props.view));
watch(() => props.view, attachToView, { flush: "post" });

// Each source is compared on its own, so a new object with the same values
// (a reassigned farmer, the config sent with each page) doesn't refresh
watch(
  [
    () => props.map,
    () => props.view,
    () => props.source,
    () => props.showLpis,
    () => props.LpisHerdNo,
    () => props.dafm?.available,
    () => cleanIds(props.linkedIds).join(","),
    // Another farmer with the same (often no) links still needs a refresh
    () => rootData().farmer?.hashid,
  ],
  () => refresh(),
  { immediate: true }
);

watch([contextShown, linkedShown], () => applyDafmVisibility());

// Ends linking, or drops a start that is still loading
function stopLinking() {
  if (ui.linking) {
    exitLinking();
  } else if (linkStarting) {
    linkSession += 1;
    linkingFarmerHashid = null;
  }
}

// The layout persists across pages, so linking ends when the page moves to
// another farmer, or to another page altogether
watch(
  () => rootData().farmer?.hashid,
  (hashid) => {
    if ((ui.linking || linkStarting) && hashid !== linkingFarmerHashid) stopLinking();
  }
);
const removeVisitListener = Inertia.on("start", (event) => {
  if (event.detail.visit.url.pathname !== window.location.pathname) stopLinking();
});

const onStartLinking = (payload) => startLinking(payload);
const onHideToggle = () => (ui.showToggle = false);
const onCancel = () => ui.linking && exitLinking();
$emitter.on("startLpisSelection", onStartLinking);
$emitter.on("hideToggleCompleteHerds", onHideToggle);
$emitter.on("cancelFarmInteraction", onCancel);

onBeforeUnmount(() => {
  $emitter.off("startLpisSelection", onStartLinking);
  $emitter.off("hideToggleCompleteHerds", onHideToggle);
  $emitter.off("cancelFarmInteraction", onCancel);
  removeVisitListener();
  clickHandle?.remove();
  // Clears $root.lpisLinking, so the phone's map/list switch comes back
  stopLinking();
});

</script>

<style scoped>
/* Inside the map container at the bottom left, above the attribution and
   other map widgets */
.lpis-link-panel {
  position: absolute;
  left: 15px;
  bottom: 30px;
  z-index: 100;
  width: 22rem;
  max-width: calc(100% - 30px);
}

.lpis-link-body {
  max-height: 60vh;
  overflow-y: auto;
}

.lpis-link-button {
  min-height: 2.5rem;
  border-radius: 0.5rem;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  font-weight: 500;
}

.lpis-link-button:hover:not(:disabled) {
  background: #f9fafb;
}

.lpis-link-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.lpis-panel {
  max-width: min(20rem, calc(100vw - 2rem));
}

/* Phones (below Tailwind's sm:, as PHONE_QUERY): a compact panel across the
   full width, still above the Esri attribution bar. The layout's round
   map/list button is hidden meanwhile ($root.lpisLinking). */
@media (max-width: 639px) {
  .lpis-link-panel {
    left: 8px;
    right: 8px;
    width: auto;
    max-width: none;
  }

  .lpis-link-button {
    min-height: 2.25rem;
    font-size: 0.75rem;
  }

  .lpis-link-body {
    max-height: 40vh;
  }
}
</style>
